import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { loginApi, type ConsoleSession } from '@/app/login/api';

interface SessionState {
  /** 아직 서버에 묻는 중이면 null. */
  session: ConsoleSession | null;
  error: string | null;
  reload: () => Promise<void>;
}

const SessionContext = createContext<SessionState | null>(null);

/** 앱 전체가 같은 세션을 본다 — 로그인 화면이 로그인을 끝내면 `reload`로 셸이 바로 새 메뉴를 받는다. */
export function SessionProvider({ children }: { children: React.ReactNode }): React.JSX.Element {
  const [session, setSession] = useState<ConsoleSession | null>(null);
  const [error, setError] = useState<string | null>(null);

  const reload = useCallback(
    () =>
      loginApi
        .current()
        .then((next) => {
          setSession(next);
          setError(null);
        })
        .catch((cause: Error) => setError(cause.message)),
    [],
  );

  useEffect(() => {
    void reload();
  }, [reload]);

  const value = useMemo(() => ({ session, error, reload }), [session, error, reload]);
  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession(): SessionState {
  const state = useContext(SessionContext);
  if (!state) throw new Error('SessionProvider 밖에서 useSession을 불렀다');
  return state;
}
